<script lang="ts">
	import { orderFilterProducts, type ProductsEndpoint } from '$lib/js/products'
	import { storeActiveFilters, storeSearchFilters } from '$lib/js/stores'
	import { cleanSearchParam, is_set_array, is_set_object } from '$lib/js/helpers'
	import { page } from '$app/state'
	import { t } from 'svelte-i18n-lingui'
	import type { BaseProduct, FullProduct, ProductQueryOptions } from '$lib/js/elasticsearch/types'
	import { getContextScope } from '$lib/js/helpers/i18n'

	interface Props {
		options: ProductQueryOptions;
		products?: (BaseProduct | FullProduct)[];
		restartBrands?: boolean;
		restartCats?: boolean;
		type?: string; // '' | search
		endpoint: ProductsEndpoint;
	}

	let {
		options = $bindable(),
		products = $bindable([]),
		restartBrands = $bindable(false),
		restartCats = $bindable(false),
		type = '',
		endpoint
	}: Props = $props();

	const scope = getContextScope()

	let max_price = $derived(type === 'search' ? $storeSearchFilters?.max_price : parseInt((page.data.max_price?.value * 1.21 + 1).toFixed(0)))

	async function restartPriceFilters() {
		options.start = 0
		options.min_price = 0
		options.max_price = max_price
		products = await orderFilterProducts(options, endpoint, { scope: scope })
		$storeActiveFilters.min_price = 0
		$storeActiveFilters.max_price = max_price

		if (type !== 'search') {
			cleanSearchParam(['minPrice', 'maxPrice'])
		}
	}

	async function restartBrandFilters() {
		options.brand_ids = []
		products = await orderFilterProducts(options, endpoint, { scope: scope })
		storeActiveFilters.update((filters) => ({ ...filters, brands: [] }))

		if (type !== 'search') {
			cleanSearchParam(['brands'])
		}

		restartBrands = !restartBrands
	}

	async function restartSubcatFilters() {
		options.cat_ids = []
		products = await orderFilterProducts(options, endpoint, { scope: scope })
		storeActiveFilters.update((filters) => ({ ...filters, subcats: [] }))

		if (type !== 'search') {
			cleanSearchParam(['cats'])
		}

		restartCats = !restartCats
	}

	async function restartFilters() {
		// Reset all options values
		options.start = 0
		options.min_price = 0
		options.max_price = 200000
		options.brand_ids = undefined
		options.cat_ids = undefined

		// Get unfiltered products
		products = await orderFilterProducts(options, endpoint, { scope: scope })

		// Reset all value and params in URL
		restartBrands = !restartBrands
		restartCats = !restartCats

		if (type !== 'search') {
			cleanSearchParam(['brands', 'cats', 'minPrice', 'maxPrice', 'sort'])
		}

		$storeActiveFilters = {}
		$storeActiveFilters.min_price = 0
		$storeActiveFilters.max_price = max_price

		// Set start to 2nd page and go to top
		options.start = 50
		document.getElementById('top-page')?.scrollIntoView({ behavior: 'smooth' })
	}
</script>

{#if is_set_object($storeActiveFilters) && ($storeActiveFilters.min_price || $storeActiveFilters.max_price || is_set_array($storeActiveFilters.brands) || is_set_array($storeActiveFilters.subcats))}
	<div class="active-filters">
		<h3 class="lead-1">
			{$t`Selected filters`}
			<button onclick={() => restartFilters()}><span class="icon icon-trash"></span></button>
		</h3>

		<div class="active-filters__wrapper">
			{#if is_set_object($storeActiveFilters) && ($storeActiveFilters.min_price || $storeActiveFilters.max_price)}
				<button onclick={() => restartPriceFilters()} class="active-filters__btn">
					<p>
						<span class="icon icon-cross"></span>
						<span class="title"> {$t`Price`}:</span>
						{$storeActiveFilters.min_price ?? 0}€ - {$storeActiveFilters.max_price ?? max_price}€
					</p>
				</button>
			{/if}

			{#if is_set_object($storeActiveFilters) && is_set_array($storeActiveFilters.brands)}
				<button onclick={() => restartBrandFilters()} class="active-filters__btn">
					<p>
						<span class="icon icon-cross"></span>
						<span class="title">{$t`Brands:`}</span>
						{$storeActiveFilters?.brands?.map((brand) => brand.page_title).join(', ')}
					</p>
				</button>
			{/if}

			{#if is_set_object($storeActiveFilters) && is_set_array($storeActiveFilters.subcats)}
				<button onclick={() => restartSubcatFilters()} class="active-filters__btn">
					<p>
						<span class="icon icon-cross"></span>
						<span class="title">{$t`Subcategories`}:</span>
						{$storeActiveFilters?.subcats?.map((cat) => cat.name).join(', ')}
					</p>
				</button>
			{/if}
		</div>
	</div>
{/if}

<style lang="scss">
  .active-filters {
    margin-top: 12px;

    h3 {
      font-size: 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      span {
        font-size: 16px;

        &:hover {
          color: var(--main);
        }
      }
    }

    &__wrapper {
      padding: 6px 10px;
      background: #0a790020;
      margin-top: 10px;
      margin-bottom: 15px;
      border-radius: 5px;
    }

    &__btn {
      p {
        text-align: left;
        /* &:first-child {
					margin-bottom: 5px;
				} */
      }

      span.icon {
        padding-right: 7px;
        color: var(--main);
        font-size: 12px;
      }

      span.title {
        color: var(--grey--b);
      }
    }
  }
</style>
